import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../../styles/Desdobramento.css';
import { getAuthSession } from '../../utils/authStorage';

// Cache com TTL (5 minutos)
const CACHE_TTL = 5 * 60 * 1000;

function salvarCacheDesdobramento(orderId, data) {
  try {
    const cache = JSON.parse(sessionStorage.getItem('desdobramentoCache') || '{}');
    cache[orderId] = {
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem('desdobramentoCache', JSON.stringify(cache));
  } catch {
    // Ignora erros de storage
  }
}

/**
 * Modal de desdobramento de pedido.
 *
 * Mostra, para um pedido "mãe", quantos pedidos "filhos" (desdobramentos)
 * foram gerados a partir dele e quanto de cada produto ainda resta —
 * ex: pedido principal com 10 tubos, 2 filhos pedindo 4 cada, sobram 2.
 */
function Desdobramento({ orderId, onClose }) {
  const [carregando, setCarregando] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [erro, setErro] = useState(null);
  const [dados, setDados] = useState(null);

  const buscarDesdobramento = useCallback(async (forcarAtualizacao = false) => {
    if (!forcarAtualizacao) {
      setCarregando(true);
    } else {
      setAtualizando(true);
    }
    setErro(null);
    try {
      const { token } = getAuthSession();
      const resp = await axios.get(`/api/orders/${encodeURIComponent(orderId)}/desdobramento`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDados(resp.data);
      // Salva no cache
      salvarCacheDesdobramento(orderId, resp.data);
    } catch (e) {
      const msg = e.response?.data?.erro || 'Não foi possível carregar o desdobramento deste pedido.';
      setErro(msg);
    } finally {
      setCarregando(false);
      setAtualizando(false);
    }
  }, [orderId]);

  useEffect(() => {
    buscarDesdobramento();
  }, [buscarDesdobramento]);

  return (
    <div className="desdobramento-overlay" onClick={onClose}>
      <div className="desdobramento-modal" onClick={(e) => e.stopPropagation()}>
        <div className="desdobramento-header">
          <h2>Desdobramento do pedido {orderId}</h2>
          <div className="desdobramento-header-actions">
            <button 
              className="desdobramento-atualizar" 
              onClick={() => buscarDesdobramento(true)}
              disabled={atualizando}
              title="Atualizar dados (recarrega do servidor)"
            >
              {atualizando ? '⟳ Atualizando...' : '⟳ Atualizar'}
            </button>
            <button className="desdobramento-fechar" onClick={onClose} aria-label="Fechar">
              ×
            </button>
          </div>
        </div>

        {carregando && (
          <div className="desdobramento-status">Carregando desdobramento…</div>
        )}

        {!carregando && erro && (
          <div className="desdobramento-status desdobramento-erro">{erro}</div>
        )}

        {!carregando && !erro && dados && (
          <div className="desdobramento-corpo">
            {dados.mensagem && (
              <p className="desdobramento-aviso">{dados.mensagem}</p>
            )}

            <section className="desdobramento-secao">
              <h3>Resumo por produto</h3>
              <table className="desdobramento-tabela">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Qtd. pedida</th>
                    <th>Qtd. desdobrada</th>
                    <th>Qtd. restante</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.resumoPorProduto.map((item) => (
                    <tr key={item.productId}>
                      <td>{item.productName}</td>
                      <td>{item.qtdPedida}</td>
                      <td>{item.qtdDesdobrada}</td>
                      <td className={item.qtdRestante > 0 ? 'desdobramento-restante' : ''}>
                        {item.qtdRestante}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {dados.pedidosFilhos.length > 0 && (
              <section className="desdobramento-secao desdobramento-secao-filhos">
                <h3>📦 Pedidos filhos / Remessa ({dados.pedidosFilhos.length})</h3>
                <ul className="desdobramento-lista-filhos">
                  {dados.pedidosFilhos.map((filho) => (
                    <li key={filho.idPedido} className={filho.cancelado ? 'desdobramento-cancelado' : ''}>
                      <div className="desdobramento-filho-cabecalho">
                        <span className="desdobramento-filho-id">Pedido {filho.idPedido}</span>
                        {filho.cancelado && <span className="desdobramento-tag">cancelado</span>}
                        {filho.erro && <span className="desdobramento-tag desdobramento-tag-erro">{filho.erro}</span>}
                        {!filho.erro && (
                          <span className="desdobramento-filho-valor">
                            {Number(filho.valorTotal || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </span>
                        )}
                      </div>
                      {!filho.erro && filho.itens && filho.itens.length > 0 && (
                        <div className="desdobramento-filho-itens">
                          {filho.itens.map((item, index) => (
                            <div key={`${filho.idPedido}-${index}`} className="desdobramento-filho-item">
                              <span>{item.productName || '—'}</span>
                              <span className="desdobramento-filho-qtd">x{item.quantity || 0}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Desdobramento;
